import { CircularProgress, Tooltip, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { LuCalendar, LuFileText, LuLink, LuLinkedin, LuMail } from "react-icons/lu";
import SearchBar from "../../../common/Search";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import CustomPagination from "../../../components/Common/CustomPagination";
import { apiGet, toastError } from "../../../utils";

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#ffffff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 250,
    fontSize: theme.typography.pxToRem(11),
    border: '1px solid #dadde9',
    boxShadow: theme.shadows[3],
    padding: '8px',
    borderRadius: '0px'
  },
}))(Tooltip);

const renderAvailabilityTooltip = (availabilityWeekly) => {
  if (!availabilityWeekly) return <div className="text-center text-slate-500 py-2">No availability set</div>;

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const hasAvailableDay = days.some(d => availabilityWeekly[d]?.isAvailable);

  if (!hasAvailableDay) {
    return <div className="text-center text-slate-500 py-2">No availability set</div>;
  }

  return (
    <div className="flex flex-col gap-2 w-48">
      <Typography variant="subtitle2" className="text-[11px] font-medium border-b border-slate-200 pb-1 mb-1 text-slate-800">Weekly Availability</Typography>
      {days.map((day) => {
        const data = availabilityWeekly[day];
        if (!data || !data.isAvailable) return null;
        return (
          <div key={day} className="flex justify-between items-center text-[11px]">
            <span className="capitalize font-medium text-slate-700">{day.substring(0, 3)}</span>
            <span className="text-slate-600 bg-slate-50 px-2 py-0.5 border border-slate-200">{data.startTime} - {data.endTime}</span>
          </div>
        );
      })}
    </div>
  );
};

export default function PartnerDetailsModal({ open, handleClose, partner }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPage, setTotalPage] = useState(0);
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  const [filters, setFilters] = useState({
    offset: 1,
    limit: 20,
    search: "",
    startDate: null,
    endDate: null,
  });

  const cacheRef = useRef({});

  useEffect(() => {
    if (!open) {
      setFilters({
        offset: 1,
        limit: 20,
        search: "",
        startDate: null,
        endDate: null,
      });
      setCalls([]);
      setTotalPage(0);
    }
  }, [open]);

  useEffect(() => {
    if (open && partner?._id) {
      const cacheKey = `${partner._id}-${filters.offset}-${filters.limit}-${filters.search}-${filters.startDate}-${filters.endDate}`;

      if (cacheRef.current[cacheKey]) {
        setCalls(cacheRef.current[cacheKey].calls);
        setTotalPage(cacheRef.current[cacheKey].totalPage);
        setLoading(false);
        return;
      }

      setLoading(true);
      const timer = setTimeout(() => {
        fetchDemoCalls(partner._id, cacheKey);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [open, partner, filters]);

  const fetchDemoCalls = async (partnerId, cacheKey) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("page", filters.offset);
      queryParams.append("limit", filters.limit);
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);

      const response = await apiGet(`/api/admin/knowledge-partner/${partnerId}/demo-bookings?${queryParams.toString()}`);
      if (response && response.data && response.data.calls) {
        setCalls(response.data.calls);
        setTotalPage(response.data.total || 0);

        cacheRef.current[cacheKey] = {
          calls: response.data.calls,
          totalPage: response.data.total || 0,
        };
      }
    } catch (err) {
      toastError(err?.message || "Failed to fetch demo calls");
    } finally {
      setLoading(false);
    }
  };

  if (!partner) return null;

  const renderCallCard = (call) => {
    return (
      <div key={call._id} className="p-2 bg-white border border-slate-200 mb-2">
        <div className="flex flex-wrap justify-between items-start gap-2 mb-1.5">
          <div className="text-[12px] font-medium text-slate-800 break-words">
            {call.requesterName} {call.requesterCompany && <span className="text-slate-500 font-medium">({call.requesterCompany})</span>}
          </div>
          <span className={`inline-flex h-[20px] items-center px-2 text-[11px] font-medium whitespace-nowrap border ${call.status === "cancelled" ? "bg-rose-50 text-rose-700 border-rose-200" :
            "bg-emerald-50 text-emerald-700 border-emerald-200"
            }`}>
            {call.status ? call.status.charAt(0).toUpperCase() + call.status.slice(1) : "Unknown"}
          </span>
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-slate-600 mb-1.5">
          <LuMail className="w-3.5 h-3.5 text-slate-400" />
          <a href={`mailto:${call.requesterEmail}`} className="hover:text-[#313C4C] hover:underline break-all">
            {call.requesterEmail}
          </a>
        </div>

        <div className="flex flex-wrap justify-between items-center gap-2 mt-1.5">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <LuCalendar className="w-3.5 h-3.5 text-slate-400" />
            <span>
              {moment(call.start).format("MMM DD, YYYY")} • {moment(call.start).format("hh:mm A")} - {moment(call.end).format("hh:mm A")}
            </span>
          </div>

          {call.meetLink && call.status !== "cancelled" && (
            <a
              href={call.meetLink}
              target="_blank"
              rel="noopener noreferrer"
              className="h-7 flex items-center gap-1.5 text-[11px] font-medium text-[#313C4C] bg-white hover:bg-slate-100 border border-slate-300 px-2 transition-colors"
              title="Open Meeting Link"
            >
              <LuLink className="w-3 h-3" />
              <span>Join</span>
            </a>
          )}
        </div>
      </div>
    );
  };



  return (
    <CustomAddModal
      open={open}
      handleClose={handleClose}
      title="Knowledge Partner Details"
      maxWidth="lg"
      onSubmitConfirm={handleClose}
      saveLabel="Close"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 h-auto md:h-[65vh] min-h-0">
        <div className="min-w-0 flex flex-col bg-white p-3 border border-slate-200 h-auto md:h-full min-h-0">

          <div className="flex items-center gap-2 w-full mb-3">
            <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#313C4C]/5 text-[#313C4C] flex items-center justify-center overflow-hidden border border-slate-100">
              {partner.profileImage ? (
                <img src={partner.profileImage} alt={partner.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-[18px] font-medium">{partner.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>

            <div className="flex flex-col items-start min-w-0">
              <Typography variant="subtitle1" className="text-[13px] font-medium text-slate-900 break-words w-full">
                {partner.name}
              </Typography>

              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 w-full">
                <LuMail className="w-3.5 h-3.5 flex-shrink-0" />
                <a href={`mailto:${partner.email}`} className="hover:text-[#313C4C] transition-colors break-all">{partner.email}</a>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
            {partner.linkedinUrl && (
              <a
                href={partner.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="h-7 flex items-center justify-center gap-1.5 px-2 bg-white text-slate-700 text-[11px] font-medium hover:bg-slate-100 transition-colors w-fit border border-slate-300"
              >
                <LuLinkedin className="w-3.5 h-3.5 text-[#313C4C]" />
                <span className="leading-none mt-0.5 text-[#313C4C]">LinkedIn Profile</span>
              </a>
            )}

            <HtmlTooltip title={renderAvailabilityTooltip(partner.availabilityWeekly)} placement="right">
              <button type="button" className="h-7 flex items-center justify-center cursor-help px-2 bg-white text-slate-700 text-[11px] font-medium hover:bg-slate-100 transition-colors w-fit border border-slate-300 gap-1.5">
                <LuCalendar className="w-3.5 h-3.5 text-[#64748b]" />
                <span className="leading-none mt-0.5 text-[#64748b]">Availability</span>
              </button>
            </HtmlTooltip>
          </div>

          <div className="w-full text-left flex flex-col min-h-[100px] md:min-h-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-1.5 text-slate-800 font-medium mb-2 flex-shrink-0 text-[11px]">
              <LuFileText className="w-3.5 h-3.5 text-slate-400" />
              Expertise & Details
            </div>
            <div className="flex-1 overflow-y-auto scroll-thin">
              <div className="text-[11px] text-slate-600 leading-relaxed whitespace-pre-wrap break-words inline">
                {!partner.smallDesc ? "No description provided." :
                  (isDescExpanded || partner.smallDesc.length <= 150) ? partner.smallDesc :
                    `${partner.smallDesc.substring(0, 150)}... `}
              </div>
              {partner.smallDesc && partner.smallDesc.length > 150 && (
                <button
                  onClick={() => setIsDescExpanded(!isDescExpanded)}
                  className="text-[#313C4C] font-medium hover:underline ml-1 cursor-pointer bg-transparent border-none p-0 inline text-[11px]"
                >
                  {isDescExpanded ? "Read Less" : "Read More"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Demo Calls */}
        <div className="md:col-span-2 min-w-0 flex flex-col h-[50vh] md:h-full bg-white border border-slate-200 overflow-hidden min-h-0">
          <div className="flex flex-wrap gap-2 border-b border-slate-200 bg-slate-50 px-2 py-2 items-center justify-between flex-shrink-0">
            <Typography variant="subtitle1" className="text-[12px] font-medium text-slate-800">
              Demo Calls History
            </Typography>
            <span className="bg-white text-slate-700 px-2 h-[20px] text-[11px] font-medium flex items-center gap-1 border border-slate-200 tabular-nums">
              Total: {totalPage}
            </span>
          </div>

          <div className="px-2 py-2 border-b border-slate-200 bg-white w-full [&_.MuiInputBase-root]:w-full [&_input]:!w-full">
            <SearchBar
              placeholder="Search requester..."
              searchValue={filters.search}
              setSearchValue={(e) => setFilters({ ...filters, search: e.target.value, offset: 1 })}
              onDateRangeChange={({ startDate, endDate }) => setFilters({ ...filters, startDate, endDate, offset: 1 })}
            />
          </div>

          <div className="flex-1 overflow-y-auto p-2 bg-slate-50 scroll-thin">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <CircularProgress size={24} className="text-[#313C4C]" />
              </div>
            ) : calls.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <LuCalendar className="w-6 h-6 mb-2 text-slate-400" />
                <Typography className="text-[11px]">No calls found.</Typography>
              </div>
            ) : (
              calls.map(renderCallCard)
            )}
          </div>

          {totalPage > filters.limit && (
            <div className="p-2 border-t border-slate-100 bg-white flex-shrink-0">
              <CustomPagination
                filters={filters}
                totalPage={totalPage}
                onChangePagination={(type) => {
                  let newOffset = filters.offset;
                  if (type === "first") newOffset = 1;
                  else if (type === "dec" && newOffset > 1) newOffset -= 1;
                  else if (type === "inc" && newOffset * filters.limit < totalPage) newOffset += 1;
                  setFilters({ ...filters, offset: newOffset });
                }}
              />
            </div>
          )}
        </div>
      </div>
    </CustomAddModal>
  );
}
